import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToVerticalAxis } from './dndModifiers';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatARS, type CategoryDto, type ItemDto } from '@gl/shared';
import { EllipsisVertical, GripVertical, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Switch } from '@/components/ui/switch';
import { cn } from '@/lib/utils';

export interface BoardActions {
  canEdit: boolean;
  onAddItem: (categoryId: string) => void;
  onEditItem: (item: ItemDto) => void;
  onDeleteItem: (item: ItemDto) => void;
  onRestoreItem: (item: ItemDto) => void;
  onEditCategory: (category: CategoryDto) => void;
  onDeleteCategory: (category: CategoryDto) => void;
  onRestoreCategory: (category: CategoryDto) => void;
  onToggleItem: (item: ItemDto, isAvailable: boolean) => void;
  onToggleVariant: (variantId: string, isAvailable: boolean) => void;
  onReorderCategories: (ordered: CategoryDto[]) => void;
  onReorderItems: (categoryId: string, ordered: ItemDto[]) => void;
}

function useBoardSensors() {
  return useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 150, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
}

/** Reordena solo los visibles y deja los ocultos al final, para mandar el orden completo. */
function reorderVisible<T extends { id: string }>(all: T[], visible: T[], e: DragEndEvent): T[] | null {
  const { active, over } = e;
  if (!over || active.id === over.id) return null;
  const from = visible.findIndex((x) => x.id === active.id);
  const to = visible.findIndex((x) => x.id === over.id);
  if (from < 0 || to < 0) return null;
  const moved = arrayMove(visible, from, to);
  const visibleIds = new Set(visible.map((x) => x.id));
  return [...moved, ...all.filter((x) => !visibleIds.has(x.id))];
}

export function CatalogBoard({
  categories,
  showArchived,
  actions,
}: {
  categories: CategoryDto[];
  showArchived: boolean;
  actions: BoardActions;
}) {
  const sensors = useBoardSensors();
  const visible = showArchived ? categories : categories.filter((c) => c.isActive);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
      onDragEnd={(e) => {
        const next = reorderVisible(categories, visible, e);
        if (next) actions.onReorderCategories(next);
      }}
    >
      <SortableContext items={visible.map((c) => c.id)} strategy={verticalListSortingStrategy}>
        <div className="grid gap-4">
          {visible.map((c) => (
            <SortableCategory key={c.id} category={c} showArchived={showArchived} actions={actions} />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}

function DragHandle({ label, ...props }: { label: string } & Record<string, unknown>) {
  return (
    <button
      type="button"
      aria-label={label}
      className="flex size-8 shrink-0 cursor-grab touch-none items-center justify-center rounded-md text-muted-foreground hover:bg-accent active:cursor-grabbing"
      {...props}
    >
      <GripVertical className="size-4" />
    </button>
  );
}

function SortableCategory({
  category,
  showArchived,
  actions,
}: {
  category: CategoryDto;
  showArchived: boolean;
  actions: BoardActions;
}) {
  const sortable = useSortable({ id: category.id, disabled: !actions.canEdit });
  const sensors = useBoardSensors();
  const items = showArchived ? category.items : category.items.filter((i) => i.isActive);
  const style = { transform: CSS.Translate.toString(sortable.transform), transition: sortable.transition };

  return (
    <section
      ref={sortable.setNodeRef}
      style={style}
      className={cn(
        'rounded-xl border bg-card shadow-xs',
        sortable.isDragging && 'relative z-10 shadow-lg ring-2 ring-primary/30',
        !category.isActive && 'border-dashed opacity-70',
      )}
    >
      <header className="flex items-center gap-2 border-b px-3 py-2.5">
        {actions.canEdit && (
          <DragHandle label={`Mover categoría ${category.name}`} {...sortable.attributes} {...sortable.listeners} />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate font-semibold">{category.name}</h2>
            {!category.isActive && <Badge variant="secondary">Eliminada</Badge>}
            <span className="text-xs text-muted-foreground">{items.length}</span>
          </div>
          {category.description && <p className="truncate text-xs text-muted-foreground">{category.description}</p>}
        </div>
        {actions.canEdit && category.isActive && (
          <Button variant="ghost" size="sm" onClick={() => actions.onAddItem(category.id)}>
            <Plus /> <span className="hidden sm:inline">Producto</span>
          </Button>
        )}
        {actions.canEdit && (
          <RowMenu label={`Opciones de ${category.name}`}>
            <DropdownMenuItem onSelect={() => actions.onEditCategory(category)}>
              <Pencil /> Editar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {category.isActive ? (
              <DropdownMenuItem variant="destructive" onSelect={() => actions.onDeleteCategory(category)}>
                <Trash2 /> Eliminar
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => actions.onRestoreCategory(category)}>
                <RotateCcw /> Restaurar
              </DropdownMenuItem>
            )}
          </RowMenu>
        )}
      </header>

      {items.length === 0 ? (
        <div className="px-4 py-6 text-center text-sm text-muted-foreground">
          Todavía no hay productos en esta categoría.
          {actions.canEdit && category.isActive && (
            <div className="mt-2">
              <Button variant="outline" size="sm" onClick={() => actions.onAddItem(category.id)}>
                <Plus /> Agregar producto
              </Button>
            </div>
          )}
        </div>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          modifiers={[restrictToVerticalAxis]}
          onDragEnd={(e) => {
            const next = reorderVisible(category.items, items, e);
            if (next) actions.onReorderItems(category.id, next);
          }}
        >
          <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
            <ul className="divide-y">
              {items.map((item) => (
                <SortableItem key={item.id} item={item} actions={actions} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>
      )}
    </section>
  );
}

function priceSummary(item: ItemDto): string {
  if (!item.variants.length) return formatARS(item.priceCents);
  const prices = item.variants.map((v) => v.priceCents);
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  const n = item.variants.length;
  const range = min === max ? formatARS(min) : `${formatARS(min)} – ${formatARS(max)}`;
  return `${n} ${n === 1 ? 'variante' : 'variantes'} · ${range}`;
}

function SortableItem({ item, actions }: { item: ItemDto; actions: BoardActions }) {
  const sortable = useSortable({ id: item.id, disabled: !actions.canEdit });
  const style = { transform: CSS.Translate.toString(sortable.transform), transition: sortable.transition };

  return (
    <li
      ref={sortable.setNodeRef}
      style={style}
      className={cn('bg-card px-3 py-2.5', sortable.isDragging && 'relative z-10 shadow-md', !item.isActive && 'opacity-60')}
    >
      <div className="flex items-center gap-2">
        {actions.canEdit && (
          <DragHandle label={`Mover ${item.name}`} {...sortable.attributes} {...sortable.listeners} />
        )}
        {item.imageUrl ? (
          <img src={item.imageUrl} alt="" loading="lazy" className="size-10 shrink-0 rounded-md bg-muted object-cover" />
        ) : null}
        <button
          type="button"
          className="min-w-0 flex-1 text-left disabled:cursor-default"
          onClick={() => actions.onEditItem(item)}
          disabled={!actions.canEdit}
        >
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <span className={cn('font-medium', !item.isAvailable && 'text-muted-foreground line-through')}>{item.name}</span>
            {!item.isAvailable && <Badge variant="outline">No disponible</Badge>}
            {!item.isActive && <Badge variant="secondary">Eliminado</Badge>}
          </div>
          <p className="truncate text-xs text-muted-foreground">{priceSummary(item)}</p>
        </button>
        {item.isActive && (
          <Switch
            checked={item.isAvailable}
            onCheckedChange={(v) => actions.onToggleItem(item, v)}
            aria-label={item.isAvailable ? `Marcar ${item.name} como no disponible` : `Marcar ${item.name} como disponible`}
          />
        )}
        {actions.canEdit && (
          <RowMenu label={`Opciones de ${item.name}`}>
            <DropdownMenuItem onSelect={() => actions.onEditItem(item)}>
              <Pencil /> Editar
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {item.isActive ? (
              <DropdownMenuItem variant="destructive" onSelect={() => actions.onDeleteItem(item)}>
                <Trash2 /> Eliminar
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onSelect={() => actions.onRestoreItem(item)}>
                <RotateCcw /> Restaurar
              </DropdownMenuItem>
            )}
          </RowMenu>
        )}
      </div>

      {item.isActive && item.variants.length > 0 && (
        <ul className={cn('mt-2 grid gap-1 sm:grid-cols-2', actions.canEdit ? 'pl-10' : 'pl-0')}>
          {item.variants.map((v) => (
            <li key={v.id} className="flex items-center justify-between gap-2 rounded-md bg-muted/50 px-2.5 py-1.5 text-sm">
              <span className={cn('min-w-0 truncate', !v.isAvailable && 'text-muted-foreground line-through')}>
                {v.name} · {formatARS(v.priceCents)}
              </span>
              <Switch
                className="scale-90"
                checked={v.isAvailable}
                onCheckedChange={(next) => actions.onToggleVariant(v.id, next)}
                aria-label={`Disponibilidad de ${item.name} ${v.name}`}
              />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}

function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="size-8" aria-label={label}>
          <EllipsisVertical />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">{children}</DropdownMenuContent>
    </DropdownMenu>
  );
}
